import React from "react";
import ReactDOM from "react-dom/client";
import App from "./app/App.tsx";
import {SaiEmbedGate} from './features/saiEmbed/SaiEmbedGate';
import "./index.css";
import { log } from "./shared/utils/logger";
import { activateLanguage, getStoredLanguage } from "./shared/i18n/config";
import { API_BASE_URL } from "./shared/apiClient";
import { configureAuditClient, trackAuditEvent } from "./shared/audit/auditClient";

configureAuditClient({ apiBaseUrl: API_BASE_URL });

// Add global error handler for debugging
window.addEventListener("error", (e) => {
  log.error("main", "Global error:", e.error);
  trackAuditEvent({
    eventType: "frontend.error",
    category: "error",
    metadata: {
      message: e.message,
      source: e.filename,
      line: e.lineno,
      column: e.colno,
    },
  });
});

window.addEventListener("unhandledrejection", (e) => {
  log.error("main", "Unhandled promise rejection:", e.reason);
  trackAuditEvent({
    eventType: "frontend.unhandled_rejection",
    category: "error",
    metadata: {
      reason: e.reason instanceof Error ? e.reason.message : String(e.reason),
    },
  });
});

const renderApplication = () => {
  const root = document.getElementById("root")!;
  // React replaces the startup shell synchronously. Clearing it here avoids
  // retaining an imperative child alongside React-owned content.
  if (root.dataset.startupShell === 'korean') {
    root.replaceChildren();
    delete root.dataset.startupShell;
  }

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <SaiEmbedGate><App /></SaiEmbedGate>
    </React.StrictMode>
  );
};

/**
 * Keeps a persisted Korean session visibly responsive while its external,
 * validated catalog is loading. This deliberately uses DOM nodes and
 * textContent rather than HTML interpolation because it runs before React.
 */
export const mountKoreanStartupLoadingShell = (): void => {
  const root = document.getElementById('root');
  if (!root) {
    return;
  }

  const shell = document.createElement('div');
  shell.setAttribute('role', 'status');
  shell.setAttribute('aria-live', 'polite');
  shell.style.cssText = 'min-height:100vh;display:flex;align-items:center;justify-content:center;background:#171717;color:#f5f5f5;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;';

  const label = document.createElement('span');
  label.textContent = '한국어 화면을 준비하고 있습니다…';
  shell.append(label);

  root.replaceChildren(shell);
  root.dataset.startupShell = 'korean';
};

const startApplication = async () => {
  if (getStoredLanguage() === 'ko') {
    document.documentElement.lang = 'ko';
    mountKoreanStartupLoadingShell();
    const activated = await activateLanguage('ko');
    if (!activated) {
      document.documentElement.lang = 'en';
      log.error('main', 'Korean catalog failed to load; rendering English');
    }
  }
  renderApplication();
};

void startApplication().catch((error) => {
  log.error("main", "Failed to render app:", error);
  document.getElementById("root")!.innerHTML = `
    <div style="padding: 20px; color: red;">
      <h1>Failed to start application</h1>
      <pre>${error}</pre>
    </div>
  `;
});
